import { useState, useRef } from 'react';
import { format, isSameDay, isAfter, isBefore, eachDayOfInterval, isWeekend } from 'date-fns';
// Holiday function now comes from useCalendarData hook via props

function useSelection(isCroatianHoliday = () => false, currentUserRole = 'employee', adminEditMode = false, collectiveVacations = []) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedRange, setSelectedRange] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [preDragSelection, setPreDragSelection] = useState([]);
  const lastSelectedRef = useRef(null);
  const mouseDownTimeRef = useRef(null);
  const dragTimeoutRef = useRef(null);

  // Helper function to check if a date is a collective vacation day
  const isCollectiveVacationDay = (date) => {
    return collectiveVacations.some(cv => {
      const cvDate = new Date(cv.date);
      return format(cvDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
    });
  };

  const clearSelection = () => {
    setSelectedDay(null);
    setSelectedRange([]);
    setSelectedUser(null);
    setIsDragging(false);
    setDragStart(null);
    setPreDragSelection([]);
    lastSelectedRef.current = null;
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  };

  const handleCellClick = (date, user, currentUser, event) => {
    // Allow admin edit mode to bypass user restriction
    if (user !== currentUser && !(currentUserRole === 'admin' && adminEditMode)) return;

    const isCtrlClick = event.ctrlKey || event.metaKey;
    const isShiftClick = event.shiftKey;

    // If we're in the middle of a drag operation, ignore clicks
    if (isDragging) return;

    if (isShiftClick && lastSelectedRef.current) {
      // Shift+click: select range from last selected to current
      const start = lastSelectedRef.current;
      const end = date;
      const range = [];

      let current = isBefore(start, end) ? start : end;
      const target = isBefore(start, end) ? end : start;

      while (!isAfter(current, target)) {
        range.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      setSelectedRange(range);
      setSelectedDay(null);
      setSelectedUser(user);
    } else if (isCtrlClick) {
      // Ctrl+click: toggle day in selection
      const isAlreadySelected = selectedRange.some(d => isSameDay(d, date));

      if (isAlreadySelected) {
        setSelectedRange(prev => prev.filter(d => !isSameDay(d, date)));
      } else {
        setSelectedRange(prev => [...prev, date]);
      }

      lastSelectedRef.current = date;
      setSelectedDay(null);
      setSelectedUser(user);
    } else {
      // Regular click: select single day
      setSelectedDay(date);
      setSelectedRange([]);
      setSelectedUser(user);
      lastSelectedRef.current = date;
    }
  };

  const handleMouseDown = (date, user, currentUser, event) => {
    // Allow admin edit mode to bypass user restriction
    if (user !== currentUser && !(currentUserRole === 'admin' && adminEditMode)) return;
    // Only respond to left mouse button
    if (event.button !== 0) return;
    // Don't start drag on modifier key clicks
    if (event.ctrlKey || event.metaKey || event.shiftKey) return;

    event.preventDefault();
    mouseDownTimeRef.current = Date.now();

    // Set a timeout to start drag mode only after a brief delay
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(true);
      setDragStart(date);
      setPreDragSelection([...selectedRange]);

      // Add the starting date to selection if not already selected
      if (!selectedRange.some(d => format(d, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'))) {
        setSelectedRange([...selectedRange, date]);
      }
      setSelectedDay(null);
      setSelectedUser(user);
    }, 150); // 150ms delay to distinguish click from drag
  };

  const handleMouseEnter = (date, user, currentUser, event) => {
    // Allow admin edit mode to bypass user restriction
    if (!isDragging || (user !== currentUser && !(currentUserRole === 'admin' && adminEditMode)) || !dragStart) return;

    // Create range from drag start to current date
    const startTime = dragStart.getTime();
    const endTime = date.getTime();
    const start = new Date(Math.min(startTime, endTime));
    const end = new Date(Math.max(startTime, endTime));

    const currentDragRange = eachDayOfInterval({ start, end });

    // Start with the pre-drag selection (what was selected before drag started)
    let newSelection = [...preDragSelection];

    // Add all workdays in the current drag range (skip weekends, holidays, and collective vacations)
    currentDragRange.forEach(dragDate => {
      // Skip weekends, Croatian holidays, and collective vacation days
      if (isWeekend(dragDate) || isCroatianHoliday(dragDate) || isCollectiveVacationDay(dragDate)) return;

      const dragDateStr = format(dragDate, 'yyyy-MM-dd');
      const alreadySelected = newSelection.some(d => format(d, 'yyyy-MM-dd') === dragDateStr);

      if (!alreadySelected) {
        newSelection.push(dragDate);
      }
    });

    // Remove duplicates
    const uniqueSelection = newSelection.filter((date, index, self) =>
      index === self.findIndex(d => format(d, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'))
    );

    setSelectedRange(uniqueSelection);
    setSelectedUser(user);
  };

  const handleMouseUp = (onDragComplete) => {
    // Clear the drag timeout if mouse is released quickly (indicates a click)
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }

    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);

      // If we have selected dates from dragging, trigger the callback
      if (selectedRange.length > 0 && onDragComplete) {
        onDragComplete(selectedRange, selectedUser);
      }
    }
  };

  const isDateSelected = (date, user) => {
    // Check if a date is selected
    const dateIsSelected = selectedRange.some(d => isSameDay(d, date)) ||
      (selectedDay && isSameDay(selectedDay, date));

    // If no date is selected, return false
    if (!dateIsSelected) return false;

    // In admin edit mode, we need to check if the cell belongs to the selected user
    if (currentUserRole === 'admin' && adminEditMode && selectedUser) {
      // Only highlight cells for the selected user
      return user === selectedUser;
    } else if (selectedUser) {
      // When not in admin edit mode or not an admin, only highlight cells for the selected user
      return user === selectedUser;
    } else {
      // Fallback case - shouldn't normally happen
      return dateIsSelected;
    }
  };

  return {
    selectedDay,
    selectedRange,
    selectedUser,
    isDragging,
    clearSelection,
    handleCellClick,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    isDateSelected,
    setSelectedRange,
    setSelectedDay
  };
}

export default useSelection;
